/**
 * CommunityVignette — 3-panel "filmstrip" showing real activity inside SlideCon
 * (chat / community post / setlist). Sits at the top of the dark "More than
 * slides" section so visitors SEE the connection story before reading cards.
 *
 * Static composition (the hero animation is the only motion loop on the page).
 * Each panel fades in on scroll with a stagger.
 */
import { motion } from "framer-motion";
import {
  MessageSquare, Heart, MessageCircle, GripVertical, Music, Check,
} from "lucide-react";

const AVATARS = {
  sarah: "https://ui-avatars.com/api/?name=Sarah+M&background=C47053&color=fff&size=64",
  james: "https://ui-avatars.com/api/?name=James+O&background=8A9A86&color=fff&size=64",
  maria: "https://ui-avatars.com/api/?name=Maria+K&background=A35A40&color=fff&size=64",
};

const RETREAT_PHOTO = "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=800&q=60";

const panel = (delay) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

function ChatMessage({ avatar, name, time, text, own = false }) {
  return (
    <div className={`flex gap-2.5 ${own ? "flex-row-reverse" : ""}`}>
      <img src={avatar} alt="" className="w-7 h-7 rounded-full flex-shrink-0" loading="lazy" />
      <div className={`max-w-[80%] ${own ? "items-end" : ""}`}>
        <div className={`flex items-baseline gap-2 mb-0.5 ${own ? "justify-end" : ""}`}>
          <span className="text-[11px] font-medium text-stone-200">{name}</span>
          <span className="text-[10px] text-stone-500">{time}</span>
        </div>
        <div
          className={`rounded-2xl px-3 py-2 text-[13px] leading-snug ${
            own
              ? "bg-[#C47053] text-white rounded-br-sm"
              : "bg-white/10 text-stone-100 rounded-bl-sm"
          }`}
        >
          {text}
        </div>
      </div>
    </div>
  );
}

function SetlistRow({ num, title, key: keySig, dragging = false }) {
  return (
    <div
      className={`flex items-center gap-3 px-2.5 py-2 rounded-lg border border-white/5 ${
        dragging ? "bg-[#C47053]/15 ring-1 ring-[#C47053]/50" : "bg-white/[0.03] hover:bg-white/[0.06]"
      } transition-colors`}
    >
      <GripVertical className={`w-3.5 h-3.5 ${dragging ? "text-[#C47053]" : "text-stone-500"}`} />
      <span className="text-[11px] font-mono text-stone-400 w-4">{num}</span>
      <span className="text-[13px] text-stone-100 flex-1 truncate">{title}</span>
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-stone-400 font-mono">{keySig}</span>
    </div>
  );
}

export default function CommunityVignette() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12" data-testid="community-vignette">
      {/* Panel 1 — Chat thread */}
      <motion.div
        {...panel(0)}
        className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 flex flex-col min-h-[300px]"
        data-testid="vignette-chat"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-stone-200" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-stone-100 leading-tight">Worship Team</p>
            <p className="text-[10px] text-stone-500 leading-tight">3 online now</p>
          </div>
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        </div>
        <div className="space-y-3 flex-1">
          <ChatMessage
            avatar={AVATARS.james}
            name="James O."
            time="9:14 AM"
            text="Can we swap 'Great Are You Lord' to Sunday's opener? Feels lighter."
          />
          <ChatMessage
            avatar={AVATARS.sarah}
            name="Sarah M. (you)"
            time="9:16 AM"
            text="Yes! Just re-ordered the setlist. Sending slides to Maria for review 🙏"
            own
          />
          <ChatMessage
            avatar={AVATARS.maria}
            name="Maria K."
            time="9:18 AM"
            text="Got it. Slides look great — I'll cue them for the 10 AM."
          />
        </div>
        <p className="text-[10px] text-stone-500 mt-3 text-center">Real-time DMs · read receipts · sound cues</p>
      </motion.div>

      {/* Panel 2 — Community post with photo */}
      <motion.div
        {...panel(0.12)}
        className="rounded-2xl bg-white/[0.04] border border-white/10 overflow-hidden flex flex-col min-h-[300px]"
        data-testid="vignette-post"
      >
        <div className="flex items-center gap-2.5 px-5 pt-5 pb-3">
          <img src={AVATARS.maria} alt="" className="w-8 h-8 rounded-full" loading="lazy" />
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-stone-100 leading-tight">Maria K.</p>
            <p className="text-[10px] text-stone-500 leading-tight">Grace Church · shared to Community · 2h</p>
          </div>
        </div>
        <div className="aspect-[16/10] bg-stone-800 overflow-hidden mx-5 rounded-lg">
          <img src={RETREAT_PHOTO} alt="Church retreat group photo" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <p className="text-[13px] text-stone-200 px-5 pt-3 leading-snug">
          Youth retreat this weekend was 🔥 — 42 students said yes to more Bible study. Thank you for praying 🙏
        </p>
        <div className="flex items-center gap-4 px-5 py-3 mt-auto text-[11px] text-stone-400">
          <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-[#C47053]" /> 28</span>
          <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> 6 replies</span>
        </div>
      </motion.div>

      {/* Panel 3 — Setlist */}
      <motion.div
        {...panel(0.24)}
        className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 flex flex-col min-h-[300px]"
        data-testid="vignette-setlist"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
            <Music className="w-4 h-4 text-stone-200" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-stone-100 leading-tight">Sunday 10 AM</p>
            <p className="text-[10px] text-stone-500 leading-tight">5 songs · 24 min</p>
          </div>
          <span className="flex items-center gap-1 text-[10px] text-emerald-400">
            <Check className="w-3 h-3" /> Synced
          </span>
        </div>
        <div className="space-y-1.5 flex-1">
          <SetlistRow num="1" title="Great Are You Lord" keySig="G" dragging />
          <SetlistRow num="2" title="Build My Life" keySig="E" />
          <SetlistRow num="3" title="Goodness of God" keySig="Ab" />
          <SetlistRow num="4" title="Amazing Grace" keySig="D" />
          <SetlistRow num="5" title="Doxology" keySig="G" />
        </div>
        <p className="text-[10px] text-stone-500 mt-3 text-center">Drag to reorder · whole team sees updates live</p>
      </motion.div>
    </div>
  );
}
